import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  CircularProgress,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FileSpreadsheet, Search } from "lucide-react";

/* -------------------------------------------------------------------
 * 1. Table Component (reused inline)
 * ------------------------------------------------------------------- */
interface TableComponentProps {
  data: any[];
  columns: string[];
  columnLabels: Record<string, string>;
}

const TableComponent: React.FC<TableComponentProps> = ({
  data,
  columns,
  columnLabels,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | null;
  }>({ key: "", direction: null });

  const mobileColumns = [
    "healthCenter",
    "caseId",
    "diagnosisResults",
    "gender",
    "timestamp",
  ];
  const tabletColumns = [
    "healthCenter",
    "deviceId",
    "caseId",
    "age",
    "gender",
    "diagnosisResults",
    "parasiteSpecies",
    "location",
    "timestamp",
  ];
  const desktopColumns = columns;

  const activeColumns = isMobile
    ? mobileColumns
    : isTablet
    ? tabletColumns
    : desktopColumns;

  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    return a[sortConfig.key] > b[sortConfig.key]
      ? dir
      : a[sortConfig.key] < b[sortConfig.key]
      ? -dir
      : 0;
  });

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderCellValue = (col: string, row: any) => {
    if (col === "timestamp") return new Date(row[col]).toLocaleString();
    if (col === "parasiteDensity" && row[col]) {
      const { value, ci } = row[col];
      if (ci && ci[0] != null && ci[1] != null)
        return `${ci[0]}–${ci[1]} parasites/µL`;
      if (value != null) return `${value} parasites/µL`;
      return "N/A";
    }
    return row[col] ?? "-";
  };

  return (
    <Box
      sx={{
        overflowX: "auto",
        maxHeight: { xs: 500, sm: 600, md: 700 },
        overflowY: "auto",
        borderRadius: 1,
      }}
    >
      <table
        className="min-w-full border"
        style={{
          fontSize: isTablet ? "0.75rem" : "0.875rem",
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          width: "100%",
          borderCollapse: "collapse",
        }}
      >
        <thead
          style={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[900]
                : theme.palette.grey[100],
            boxShadow:
              theme.palette.mode === "dark"
                ? "0px 2px 4px rgba(255,255,255,0.05)"
                : "0px 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <tr>
            {activeColumns.map((col) => (
              <th
                key={col}
                onClick={() => handleSort(col)}
                style={{
                  padding: isTablet ? "6px 8px" : "8px 12px",
                  cursor: "pointer",
                  fontWeight: 700,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  color: theme.palette.text.primary,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                {columnLabels[col] || col}
                {sortConfig.key === col &&
                  (sortConfig.direction === "asc" ? " ↑" : " ↓")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, i) => (
            <tr key={i}>
              {activeColumns.map((col) => (
                <td
                  key={col}
                  style={{
                    padding: isTablet ? "6px 8px" : "8px 12px",
                    borderTop: `1px solid ${theme.palette.divider}`,
                    whiteSpace: "nowrap",
                    color: theme.palette.text.secondary,
                  }}
                >
                  {renderCellValue(col, row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

/* -------------------------------------------------------------------
 * 2. Positive Cases Page
 * ------------------------------------------------------------------- */
const PositiveCasesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [allTests, setAllTests] = useState<any[]>([]);
  const [filters, setFilters] = useState({ gender: "", location: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Use environment variable for API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  /* ----- Fetch all tests ----- */
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/tests`)
      .then((res) => {
        if (!res.ok) throw new Error("Network error");
        return res.json();
      })
      .then((data) => {
        setAllTests(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching tests:", err);
        setLoading(false);
      });
  }, [API_BASE_URL]);

  /* ----- Filter to Positive only ----- */
  const positiveTests = allTests.filter(
    (t) => t.diagnosisResults === "Positive"
  );

  const filteredTests = positiveTests.filter((test) => {
    const matchesFilters =
      (filters.gender === "" || test.gender === filters.gender) &&
      (filters.location === "" || test.location === filters.location);

    const matchesSearch =
      searchTerm === "" ||
      Object.values({
        caseId: test.caseId,
        healthCenter: test.healthCenter,
        location: test.location,
        parasiteSpecies: test.parasiteSpecies,
      })
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    return matchesFilters && matchesSearch;
  });

  const totalPages = Math.ceil(filteredTests.length / pageSize);
  const paginatedTests = filteredTests.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  /* Reset pagination when filters/search change */
  useEffect(() => {
    setPage(1);
  }, [filters, pageSize, searchTerm]);

  /* ----- CSV Export ----- */
  const exportCSV = () => {
    const headers = [
      "Health Center",
      "Device ID",
      "Case ID",
      "Age",
      "Gender",
      "Diagnosis",
      "Species",
      "Parasite Density (Range)",
      "Drug Resistance",
      "Location",
      "Timestamp",
    ];

    const rows = filteredTests.map((t) => [
      t.healthCenter,
      t.deviceId,
      t.caseId,
      t.age,
      t.gender,
      t.diagnosisResults,
      t.parasiteSpecies,
      t.parasiteDensity?.ci
        ? `${t.parasiteDensity.ci[0]}–${t.parasiteDensity.ci[1]} parasites/µL`
        : t.parasiteDensity?.value
        ? `${t.parasiteDensity.value} parasites/µL`
        : "N/A",
      t.drugResistance,
      t.location,
      new Date(t.timestamp).toLocaleString(),
    ]);

    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "MariTest_PositiveCases.csv";
    link.click();
  };

  /* ----- Render UI ----- */
  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={{ xs: 2, sm: 3 }}
        mb={{ xs: 2, sm: 3 }}
      >
        <Typography
          variant={isMobile ? "h6" : isTablet ? "h5" : "h4"}
          sx={{ fontWeight: 700, color: theme.palette.text.primary }}
        >
          Positive Cases
        </Typography>

        <Button
          variant="outlined"
          startIcon={<FileSpreadsheet size={16} />}
          onClick={exportCSV}
        >
          Export CSV
        </Button>
      </Stack>

      <Card
        sx={{
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[2],
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <>
              {/* Filters & Search */}
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                mb={3}
                alignItems="center"
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by Case ID, Health Center, Location, or Species..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Search size={18} style={{ marginRight: 8 }} />
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={filters.gender}
                    onChange={(e) =>
                      setFilters({ ...filters, gender: e.target.value as string })
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={filters.location}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        location: e.target.value as string,
                      })
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Arua">Arua</MenuItem>
                    <MenuItem value="Kampala">Kampala</MenuItem>
                    <MenuItem value="Luwero">Luwero</MenuItem>
                    <MenuItem value="Mbarara">Mbarara</MenuItem>
                    <MenuItem value="Gulu">Gulu</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              {/* Table */}
              <TableComponent
                data={paginatedTests}
                columns={[
                  "healthCenter",
                  "deviceId",
                  "caseId",
                  "age",
                  "gender",
                  "diagnosisResults",
                  "parasiteSpecies",
                  "parasiteDensity",
                  "drugResistance",
                  "location",
                  "timestamp",
                ]}
                columnLabels={{
                  healthCenter: "Health Center",
                  deviceId: "Device ID",
                  caseId: "Case ID",
                  age: "Age",
                  gender: "Gender",
                  diagnosisResults: "Diagnosis",
                  parasiteSpecies: "Species",
                  parasiteDensity: "Parasite Density (Range)",
                  drugResistance: "Drug Resistance",
                  location: "Location",
                  timestamp: "Timestamp",
                }}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  mt={3}
                >
                  <FormControl
                    size="small"
                    sx={{ minWidth: 120, mb: { xs: 1, sm: 0 } }}
                  >
                    <InputLabel>Rows</InputLabel>
                    <Select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                      <MenuItem value={200}>200</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                    </Select>
                  </FormControl>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page === 1}
                      onClick={() => setPage(1)}
                    >
                      First
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    >
                      Prev
                    </Button>
                    <Typography>
                      Page {page} of {totalPages}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page === totalPages}
                      onClick={() => setPage(totalPages)}
                    >
                      Last
                    </Button>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PositiveCasesPage;
